import { spacing } from '@scality/core-ui';
import { Heatmap as CoreUIHeatmap, type HeatmapLength } from '@scality/core-ui/dist/next';
import type { CoreUITheme } from '@scality/core-ui/dist/style/theme';
import styled, { useTheme } from 'styled-components';
import { ChartCard } from '../../chat-messages/ChartWrapper';
import type { AIToolComponent } from '../../chat-messages/GuardianMarkdown';
import { ResolveInstanceMarkers } from '../../ResolveInstanceMarkers';
import { NoData } from '../NoData';
import { buildStatusGridTable } from '../shared/chartExportTables';

/** Declared order, which is also the legend's: healthiest first, absence last. */
const STATUSES = ['OK', 'Warning', 'Critical', 'No data'] as const;
type HeatmapStatus = (typeof STATUSES)[number];

/** What a slot reports when nothing was collected for it — a value, not a blank. */
const NO_DATA: HeatmapStatus = 'No data';

type HeatmapRowParams = {
  label: string;
  /** Read positionally against `columns`; `null` leaves the slot empty. */
  cells: (HeatmapStatus | null)[];
};

type HeatmapProps = {
  title: string;
  legendTitle?: string;
  /** Column starts, unix timestamps in milliseconds. */
  columns: number[];
  rows: HeatmapRowParams[];
};

const STATUS_TOKEN: Record<HeatmapStatus, keyof CoreUITheme> = {
  OK: 'statusHealthy',
  Warning: 'statusWarning',
  Critical: 'statusCritical',
  // A feature the entity does not have is neither good nor bad news, so it takes no status colour.
  'No data': 'textSecondary',
};

/**
 * Severity order, not alphabetical: the legend reads OK → Critical the way an operator scans it.
 * A comparator rather than core-ui's `'status'` preset, which whitelists three names of its own
 * and would drop `No data` from the legend while still painting its cells.
 */
const bySeverity = (a: string, b: string) =>
  STATUSES.indexOf(a as HeatmapStatus) - STATUSES.indexOf(b as HeatmapStatus);

/**
 * At most this many x-axis ticks.
 *
 * Set by the widest *line* a tick holds, which is the date — a tick carrying a time too
 * stacks the two rather than widening, so the cap follows `27 Sept` and not
 * `27 Sept 15:00`.
 */
const MAX_TICKS = 14;

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Label steps that read as round durations. A step is picked from this ladder rather than
 * from `columns.length / MAX_TICKS`, because an arbitrary one drifts across the clock: 21
 * hourly columns is a label every 21 hours, which lands at 12:00, then 09:00, then 06:00,
 * and no two ticks of a week share a time of day.
 */
const TICK_STEPS = [
  MINUTE,
  5 * MINUTE,
  15 * MINUTE,
  30 * MINUTE,
  HOUR,
  2 * HOUR,
  3 * HOUR,
  4 * HOUR,
  6 * HOUR,
  8 * HOUR,
  12 * HOUR,
  DAY,
  2 * DAY,
  3 * DAY,
  7 * DAY,
  14 * DAY,
  28 * DAY,
];

/** How many columns apart the labels sit: the roundest step that keeps them under the cap. */
export const labelEveryFor = (columns: number[]): number => {
  if (columns.length < 2) return 1;
  const columnStep = columns[1] - columns[0];
  const minimum = columnStep * Math.ceil(columns.length / MAX_TICKS);
  const step = TICK_STEPS.find((candidate) => candidate >= minimum);
  // Past the ladder — a span of months — the cap is what matters, not roundness.
  return step ? Math.round(step / columnStep) : Math.ceil(columns.length / MAX_TICKS);
};

const TIME_FORMAT = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' });
const DATE_FORMAT = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' });

/**
 * A tick's label: the date where the day turns, the time within a day.
 *
 * core-ui reads the *slot* duration to choose, which says nothing here: a week of hourly
 * slots would be labelled in times alone, and a reader looking at 12:00 could not tell
 * which of the seven days it belongs to.
 *
 * The one tick carrying both stacks them. `AxisTick` is `white-space: nowrap`, so a tick
 * overflows its column rather than wrapping — and a date beside a time is wide enough to
 * run into its neighbour. A forced break is unaffected by `nowrap`, and halves the width
 * that sets how many ticks fit.
 */
/**
 * The two lines of a stacked tick, centred on each other.
 *
 * `inline-block` because the tick's own `text-align` centres each line in the *column*, and a
 * column is narrower than either line — so the lines end up centred on the column and ragged
 * against each other. Shrink-wrapping them first gives `text-align` a box the width of the
 * longer line to centre the shorter one in.
 */
const StackedTick = styled.span`
  display: inline-block;
  text-align: center;
`;

const formatTick = (column: Date, previous: Date | undefined, step: number): React.ReactNode => {
  if (step >= DAY) return DATE_FORMAT.format(column);
  const dayTurned = previous === undefined || previous.getDate() !== column.getDate();
  if (!dayTurned) return TIME_FORMAT.format(column);
  return (
    <StackedTick>
      {DATE_FORMAT.format(column)}
      <br />
      {TIME_FORMAT.format(column)}
    </StackedTick>
  );
};

/**
 * Title on the left, the card's controls on the right.
 *
 * Enlarging a heatmap buys width rather than height: the cells keep their size and more of
 * them fit before the grid has to scroll, which is what a dense axis runs out of first.
 */
const HeatmapHeader = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.r8};
`;

const HeatmapView: React.FC<HeatmapProps> = ({ title, legendTitle, columns, rows }) => {
  const theme = useTheme() as CoreUITheme;

  // A grid holding nothing but the absence value is an axis drawn over an outage, and a
  // wall of grey reads as a rendering fault rather than as "nothing was collected".
  const hasReading = rows.some((row) => row.cells.some((cell) => cell !== null && cell !== NO_DATA));
  if (columns.length === 0 || !hasReading) {
    return (
      <ChartCard>
        <NoData title={title} />
      </ChartCard>
    );
  }

  const colorSet = Object.fromEntries(STATUSES.map((status) => [status, theme[STATUS_TOKEN[status]]]));

  const cellHeight: HeatmapLength = rows.length === 1 ? '4rem' : '2rem';

  const labelEvery = labelEveryFor(columns);
  const dates = columns.map((timestamp) => new Date(timestamp));
  const stepMs = columns.length > 1 ? (columns[1] - columns[0]) * labelEvery : 0;
  // Keyed by index, so a tick knows the labelled column before it and can drop the date
  // that one already carries.
  const labels = new Map(
    dates
      .filter((_, index) => index % labelEvery === 0)
      .map((column, order, labelled) => [column.getTime(), formatTick(column, labelled[order - 1], stepMs)]),
  );

  return (
    <ChartCard title={title} buildExportTable={() => buildStatusGridTable({ name: title, columns, rows })}>
      {(view) => (
        <CoreUIHeatmap
          // core-ui's heatmap has no `rightTitle`, so the card's controls ride in the title.
          title={
            <HeatmapHeader>
              {title}
              {view.actions}
            </HeatmapHeader>
          }
          legendTitle={legendTitle ?? 'Status'}
          rows={rows}
          columns={dates}
          labelEvery={labelEvery}
          formatColumnTick={(column) => labels.get(column.getTime()) ?? ''}
          scale={{ colorSet, sortOrder: bySeverity }}
          cellHeight={cellHeight}
        />
      )}
    </ChartCard>
  );
};

/**
 * A status history as a grid: one row per entity, one column per time slot.
 *
 * What `MetricCards` cannot say about a health code — a card holds the latest value, so it reports
 * that a volume is critical now but not since when, nor that it has been flapping all morning.
 *
 * Row labels name a deployment where several are in scope, and the gutter takes a string rather
 * than a node, so the markers resolve to names before the grid is built.
 */
export const Heatmap: AIToolComponent<HeatmapProps> = ({ params }) => (
  <ResolveInstanceMarkers value={params.rows} render={(rows) => <HeatmapView {...params} rows={rows} />} />
);

Heatmap.description =
  'Displays a status history as a grid: one row per entity, one column per time slot, each cell ' +
  'coloured by the state that entity was in. Use it for a health state across several entities ' +
  'over time — per-volume or per-ring statuses, or a row per monitoring service. Values are names, ' +
  'not numbers: use a LineChart for anything measured.';

Heatmap.schema = {
  type: 'object',
  properties: {
    title: { type: 'string', description: 'The title of the chart.' },
    legendTitle: { type: 'string', description: "Heading above the legend. Defaults to 'Status'." },
    columns: {
      type: 'array',
      minItems: 1,
      description: 'Column starts, unix timestamps in milliseconds, ascending. Defines the x-axis.',
      items: { type: 'integer' },
    },
    rows: {
      type: 'array',
      minItems: 1,
      items: {
        type: 'object',
        properties: {
          label: { type: 'string', description: 'What the row is about — an entity, or a metric.' },
          cells: {
            type: 'array',
            description:
              'One cell per column, read positionally: cell i sits under column i. null leaves the slot ' +
              'empty, for a column this row reported nothing in.',
            items: { type: ['string', 'null'], enum: [...STATUSES, null] },
          },
        },
        required: ['label', 'cells'],
      },
    },
  },
  required: ['title', 'columns', 'rows'],
  additionalProperties: false,
};

Heatmap.displayInPanel = true;
